import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Autocomplete,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';

interface ISiteVisit {
  visitId: string;
  siteId: string;
  type: number;
  user: string | null;
  customerNo: string;
  reportedOn: string;
  isManualEntry: number;
}

interface ISiteVisitFilterData {
  siteIds: string[];
  users: string[];
  accountNumbers: string[];
}

const ManualSiteVisit: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [filterData, setFilterData] = useState<ISiteVisitFilterData | null>(null);
  const [loading, setLoading] = useState(false);
  const [recordedVisits, setRecordedVisits] = useState<ISiteVisit[]>([]);
  const [siteId, setSiteId] = useState('');
  const [type, setType] = useState<number | null>(null);
  const [customerNo, setCustomerNo] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const loadFilterData = useCallback(async () => {
    try {
      const response = await axios.get(`${environment.apiUrl}visit/filterdata`);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  }, []);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  const handleAddVisit = async () => {
    if (!siteId || type === null || !customerNo) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const newVisit: ISiteVisit = {
      visitId: uuidv4(),
      siteId,
      type: type as number,
      user: null, // Angular sends null for user
      customerNo,
      reportedOn: new Date().toISOString(),
      isManualEntry: 1
    };

    try {
      await axios.post(`${environment.apiUrl}visit`, [newVisit]);
      setSuccess('Visit successfully recorded');
      // Add to recorded visits list (at the beginning, limit to 10)
      setRecordedVisits(prev => [newVisit, ...prev].slice(0, 10));
      setCustomerNo(''); // Reset only customer number for next entry, keep siteId and type
    } catch (error) {
      console.error('Error submitting visit:', error);
      setError('Network error. Try again later');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }} gutterBottom>
        Record Manual Visits
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Visit Details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.siteIds || []}
              value={siteId || null}
              onChange={(event, newValue) => {
                setSiteId(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Site ID" required />
              )}
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={[
                { label: 'Entrance', value: 1 },
                { label: 'Exit', value: 2 }
              ]}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
              value={type !== null ? { label: type === 1 ? 'Entrance' : 'Exit', value: type } : null}
              onChange={(event, newValue) => {
                setType(newValue ? (newValue as { label: string; value: number }).value : null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Type" required />
              )}
              isOptionEqualToValue={(option, value) => 
                (option as { label: string; value: number }).value === (value as { label: string; value: number }).value
              }
            />
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="Customer No"
              value={customerNo}
              onChange={(e) => setCustomerNo(e.target.value)}
              size="small"
              required
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddVisit}
            disabled={loading}
            sx={{ minWidth: '120px', height: '40px', alignSelf: 'flex-start' }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Submitting...' : 'Add Visit'}
          </Button>
        </Box>
      </Paper>

      {recordedVisits.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recorded Visits ({recordedVisits.length})
            </Typography>
          </Box>
          
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
            {recordedVisits.map((visit) => (
              <Card key={visit.visitId} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Site ID:</Typography>
                      <Typography variant="body2" fontWeight={600}>{visit.siteId}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Customer No:</Typography>
                      <Typography variant="body2" fontWeight={500}>{visit.customerNo}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Type:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {visit.type === 1 ? 'Entrance' : visit.type === 2 ? 'Exit' : visit.type}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Date Time:</Typography>
                      <Typography variant="body2" fontWeight={500}>{formatDateTime(visit.reportedOn)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Desktop Table View */}
          <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
            <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Site ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Customer No</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Date Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recordedVisits.map((visit) => (
                  <TableRow 
                    key={visit.visitId}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <TableCell sx={{ wordBreak: 'break-word' }}>{visit.siteId}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{visit.customerNo}</TableCell>
                    <TableCell>{visit.type === 1 ? 'Entrance' : visit.type === 2 ? 'Exit' : visit.type}</TableCell>
                    <TableCell>{formatDateTime(visit.reportedOn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default ManualSiteVisit;
